<template>
  <PageHeader :showBreadcrumb="true" />
  <p>
    Welcome to the Managed Care Systems portal!
  </p>
  <p>
    This portal is designed to allow health plans and pharmacy benefit managers to upload information for MDH to review through one single portal.
    Currently, the Network Adequacy portal is live, for uploading of annual Network Adequacy documentation.
    The portal will be updated over the next year with modules for additional network adequacy submissions, HMO annual supplemental reporting, PBM network adequacy review submission, HMO licensure updates, and HMO quality exam documents.
    If you have any questions, please email <a href="mailto:health.healthplan@state.mn.us">health.healthplan@state.mn.us</a>.
  </p>
  <div v-if="!appStore.loading && !isFormEnabled" class="container p-3 text-center">
    <h3>Please select a Health Care Entity to proceed using the Manage Account link above.</h3>
  </div>
  <div v-else-if="appStore.loading" class="container p-3 text-center">
    <p>Loading data...</p>
  </div>
  <div v-else-if="isFormEnabled" class="container p-3 text-center">
    <div class="row">
      <div class="col-lg-6 col-md-12">
        <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
          <h2 class="tx-col">Network Adequacy</h2>
          <Button size="lg" class="me-2" rounded raised label="Go to Network Adequacy" severity="primary" @click="router.push('/NetworkAdequacy');"/>
        </div>
      </div>
      <div class="col-lg-6 col-md-12">
        <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
          <h2 class="tx-col">Network Adequacy for Pharmacy Benefit Managers (PBMs)</h2>
          <Button size="lg" class="me-2" rounded raised label="Go to Network Adequacy For PBMs" severity="primary" @click="router.push('/NetworkAdequacyForPBMs');"/>
        </div>
      </div>
      <div class="col-lg-6 col-md-12">
        <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
          <h2 class="tx-col">Annual Required Filings and Forms</h2>
          <Button size="lg" class="me-2" rounded raised label="Go to Annual Required Filings And Forms" severity="primary" @click="router.push('/AnnualRequiredFilingsAndForms');"/>
        </div>
      </div>
      <!--<div class="col-lg-6 col-md-12">
        <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
          <h2 class="tx-col">Health insurance licensing updates</h2>
          <Button size="lg" class="me-2" rounded raised label="Go to Health Insurance Licensing Updates" severity="primary" @click="router.push('/HealthInsuranceLicensingUpdates');"/>
        </div>
      </div>
      <div class="col-lg-6 col-md-12">
        <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
          <h2 class="tx-col">Uploads for Quality Assurance exams</h2>
          <Button size="lg" class="me-2" rounded raised label="Go to Uploads For Quality Assurance Exams" severity="primary" @click="router.push('/UploadsForQualityAssuranceExams');"/>
        </div>
      </div>
      <div class="col-lg-6 col-md-12">
        <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
          <h2 class="tx-col">HMO Transactions</h2>
          <Button size="lg" class="me-2" rounded raised label="Visit HMO Transactions Portal" severity="primary" @click="router.push('/HMOTransactions');"/>
        </div>
      </div>-->
      <div v-if="associatedHmoOrCbpCount > 0" class="col-lg-6 col-md-12">
        <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
          <h2 class="tx-col">Licensing Updates & Other Required Reporting</h2>
          <Button size="lg" class="me-2" rounded raised label="Go to Update Licensing & Other Documents" severity="primary" @click="router.push('/UpdatesLicensingDocuments');"/>
        </div>
      </div>
      <div v-if="userStore.isStaffUser" class="col-lg-6 col-md-12">
        <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
          <h2 class="tx-col">Document Management</h2>
          <Button size="lg" class="me-2" rounded raised label="Go to Document Management Table" severity="primary" @click="router.push('/DocumentManagementTable');"/>
        </div>
      </div>
      <div v-if="userStore.isStaffUser" class="col-lg-6 col-md-12">
        <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
          <h2 class="tx-col">Quality Assurance</h2>
          <Button size="lg" class="me-2" rounded raised label="Go to Quality Assurance" severity="primary" @click="router.push('/QualityAssurance');"/>
        </div>
      </div>
    </div>
  </div>
  <Dialog v-model:visible="isEntityQuestionOpen" modal header="Welcome to the Managed Care System Portal" :closable="false" :style="{ width: '30rem' }">
    <div class="row mb-8">
      <div class="col-2">
        <i :class="[ICONS.QuestionCircle, 'color-success', 'fs-1']"></i>
      </div>
      <div class="col-10">
        <p>
          There are currently no health care entities associated with your account.<br />
          Please select one or more on the next screen.
        </p>
      </div>
    </div>
    <template #footer>
      <Button class="ml-auto" type="button" rounded raised autofocus @click="closeDialog();router.push('/ManageAccount')" label="Ok" severity="primary"/>
    </template>
  </Dialog>
</template>

<script setup>

import {useAppStore} from '@/stores/appStore';
import {useDialogStore} from '@/stores/dialogStore.js';
import {ENTITY_TYPES, ICONS} from "@/utils/constants.js";
import {computed, ref, watch} from "vue";
import router from "@/router/index.js";
import PageHeader from "@/components/common/PageHeader.vue";
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import {useAuthStore} from "@/stores/authStore.js";

const appStore = useAppStore();
const dialogStore = useDialogStore();
const userStore = useAuthStore();

// Booleans which control dialogs
const hasTriggeredEntityQuestion = ref(false);
const isFormEnabled = computed(() => {
  return appStore.userEntities.length > 0;
});
const associatedHmoOrCbpCount = computed(() => {
  return appStore.getUserEntitiesByTypes([ENTITY_TYPES.HMO, ENTITY_TYPES.CBP]).length;
});

// Wait for loading of user-entity relationships to finish in the background before showing a dialog, since this page may display before async loading into the appStore is complete.
watch(
  [() => appStore.loading, () => appStore.userEntities.length],
  ([newLoading, newLength]) => {
    if (!newLoading && newLength === 0 && !hasTriggeredEntityQuestion.value) {
      toggleDialog('entityQuestion');
      hasTriggeredEntityQuestion.value = true;  // Set the flag to true so it doesn't trigger again
    }
  }
);

// Dialog Controls
const toggleDialog = (name) => dialogStore.toggle(name);
const closeDialog = () => dialogStore.close();
const isEntityQuestionOpen = computed(() => dialogStore.isOpen('entityQuestion'));




</script>

<style scoped>
.portal-box {
  height: 200px;
}
.tx-col {
  height: 75%;
}
</style>